import Button from '2-core-elements/Buttons/Button';
import debounce from 'lodash.debounce';
import { useEffect, useRef, type FC } from 'react';
import { BREAKPOINT_SM } from 'utils/breakpoint';
import { generateYearRangeItems, getDefaultFilters } from './ListingFilterHelpers';
import ListingFilterInput from './ListingFilterInput';
import ListingFilterSelect from './ListingFilterSelect';
import { type Filter, type ListingFiltersState } from './ListingFiltersTypes';


export interface ListingFiltersProps extends ListingFiltersState {
    filters: Filter[]; // settings
    disabled?: boolean;
    clearFilterLabel?: string;
    applyFilterLabel?: string;
    filterModalHeadingLabel?: string;
    resetMapZoomRef?: React.MutableRefObject<(() => void) | null>;
}

const DEBOUNCE_VALUE = 100;

const ListingFilters: FC<ListingFiltersProps> = ({
    filters,
    activeFilters,
    setActiveFilters,
    disabled,
    setFilterModalOpen,
    filterModalOpen,
    clearFilterLabel = 'Clear',
    applyFilterLabel = 'Apply',
    filterModalHeadingLabel = 'Filter',
    resetMapZoomRef,
}) => {
    const isOpenRef = useRef(filterModalOpen);

    useEffect(() => {
        if (!filterModalOpen) {
            isOpenRef.current = false;
            document.body.classList.remove('mobile-modal-opened');
        } else {
            isOpenRef.current = true;
            document.body.classList.add('mobile-modal-opened');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterModalOpen]);

    const windowResizeCheck = (): void => {
        if (window.innerWidth >= BREAKPOINT_SM && !!isOpenRef.current) {
            setFilterModalOpen(false);
            document.body.classList.remove('mobile-modal-opened');
        }
    };

    useEffect(() => {
        windowResizeCheck();
        window.addEventListener('resize', debounce(windowResizeCheck, DEBOUNCE_VALUE));

        return () => {
            window.removeEventListener('resize', debounce(windowResizeCheck, DEBOUNCE_VALUE));
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const openClass = filterModalOpen ? 'is-open' : '';

    return (
        <div className={`listing-app-filter-modal ${openClass}`}>
            <div className="listing-app-filter-modal-container">
                <div className="listing-app-filter-modal-content">
                    <div className="listing-app-filter-modal-header hidden-md">
                        <h4 className="listing-app-filter-modal-header-title">{filterModalHeadingLabel}</h4>
                        <div className="listing-app-filter-modal-header-cta">
                            <Button
                                onClick={(): void => {
                                    setFilterModalOpen(false);
                                }} skin="2" onlyIcon={true} prefixIcon="x" size="sm" ariaLabel="Close"
                            />
                        </div>
                    </div>
                    <div className={`listing-app-filter cols-${filters.length}`}>
                        {filters.map(({ placeholder, field, items, type, start, end, label }) => {
                            const key = `${placeholder}-${field}`;

                            switch (type) {
                            case 'input': {
                                return (
                                    <ListingFilterInput
                                        key={key}
                                        placeholder={placeholder}
                                        field={field}
                                        label={label}
                                        activeFilters={activeFilters}
                                        setActiveFilters={setActiveFilters}
                                        disabled={disabled}
                                        suffixIcon="search"
                                        maxLength={30}
                                    />
                                );
                            }
                            case 'year-select': {
                                const startYear = start || 1960;
                                const yearItems = generateYearRangeItems(startYear, end);

                                return (
                                    <ListingFilterSelect
                                        key={key}
                                        placeholder={placeholder}
                                        field={field}
                                        label={label}
                                        activeFilters={activeFilters}
                                        setActiveFilters={setActiveFilters}
                                        items={yearItems || []}
                                        disabled={disabled}
                                    />
                                );
                            }
                            // default to 'select'
                            default: {
                                return (
                                    <ListingFilterSelect
                                        key={key}
                                        placeholder={placeholder}
                                        field={field}
                                        label={label}
                                        activeFilters={activeFilters}
                                        setActiveFilters={setActiveFilters}
                                        items={items || []}
                                        disabled={disabled}
                                    />
                                );
                            }
                            }
                        })}
                    </div>
                    <div className="listing-app-filter-ctas hidden-md">
                        <Button
                            onClick={(): void => {
                                setFilterModalOpen(false);
                            }}
                        >
                            {applyFilterLabel}
                        </Button>
                        <Button
                            skin="2"
                            prefixIcon="restart"
                            onClick={(): void => {
                            // reset all filters
                                setActiveFilters(getDefaultFilters(filters));
                                // reset map zoom to default
                                resetMapZoomRef?.current?.();
                                // close the modal on mobile
                                setFilterModalOpen(false);
                            }} disabled={disabled}
                        >
                            {clearFilterLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListingFilters;
